import React, { useState } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

interface TimeSlot {
  time: string;
  available: boolean;
}

interface DaySchedule {
  date: string;
  day: string;
  slots: TimeSlot[];
}

interface ProductDetailTourSchedulingModalProps {
  open: boolean;
  onClose: () => void;
  propertyPrice?: number;
}

export const ProductDetailTourSchedulingModal = ({
  open,
  onClose,
  propertyPrice = 500000,
}: ProductDetailTourSchedulingModalProps) => {
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  // Mock available dates and times (next 7 days)
  const availableDates: DaySchedule[] = [
    {
      date: '2024-01-15',
      day: 'Mon',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: false },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
    {
      date: '2024-01-16',
      day: 'Tue',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: false },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
    {
      date: '2024-01-17',
      day: 'Wed',
      slots: [
        { time: '9:00 AM', available: false },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: false },
      ],
    },
    {
      date: '2024-01-18',
      day: 'Thu',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
    {
      date: '2024-01-19',
      day: 'Fri',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: false },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
  ];

  // Mock comparable properties
  const comparableProperties = [
    {
      id: '1',
      address: '1234 Oak Street, Beverly Hills, CA',
      price: 485000,
      beds: 3,
      baths: 2,
      sqft: 1850,
    },
    {
      id: '2',
      address: '5678 Maple Ave, Beverly Hills, CA',
      price: 520000,
      beds: 3,
      baths: 2.5,
      sqft: 1950,
    },
    {
      id: '3',
      address: '9012 Pine Blvd, Beverly Hills, CA',
      price: 495000,
      beds: 3,
      baths: 2,
      sqft: 1800,
    },
  ];

  const handlePropertyToggle = (propertyId: string) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const getSelectedDateSchedule = () => {
    return availableDates.find((date) => date.date === selectedDate);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalCreditsNeeded = selectedProperties.length + 1; // +1 for main property
  const hasEnoughCredits = totalCreditsNeeded <= 6;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)`,
          border: '2px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          height: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'white',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon sx={{ color: '#e91e63' }} />
          <Typography variant="h5" fontWeight={600}>
            Schedule Property Tour
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="6 Credits Available"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.2),
              color: 'white',
              fontSize: '0.75rem',
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column - Calendar & Time Selection */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" color="white" gutterBottom>
              Select Date & Time
            </Typography>

            {/* Date Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="rgba(255, 255, 255, 0.8)"
                gutterBottom
              >
                Available Dates
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {availableDates.map((date) => (
                  <Paper
                    key={date.date}
                    onClick={() => setSelectedDate(date.date)}
                    sx={{
                      p: 2,
                      minWidth: 80,
                      textAlign: 'center',
                      cursor: 'pointer',
                      background:
                        selectedDate === date.date
                          ? `linear-gradient(135deg, ${alpha(
                              theme.palette.error.main,
                              0.2
                            )} 0%, ${alpha(
                              theme.palette.error.main,
                              0.1
                            )} 100%)`
                          : 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${
                        selectedDate === date.date
                          ? theme.palette.error.main
                          : 'rgba(255, 255, 255, 0.1)'
                      }`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.error.main,
                          0.1
                        )} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                        borderColor: theme.palette.error.main,
                      },
                    }}
                  >
                    <Typography variant="h6" color="white" fontWeight={600}>
                      {date.day}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="rgba(255, 255, 255, 0.7)"
                    >
                      {new Date(date.date).getDate()}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>

            {/* Time Selection */}
            {selectedDate && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="rgba(255, 255, 255, 0.8)"
                  gutterBottom
                >
                  Available Times
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {getSelectedDateSchedule()?.slots.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={
                        selectedTime === slot.time ? 'contained' : 'outlined'
                      }
                      disabled={!slot.available}
                      onClick={() => setSelectedTime(slot.time)}
                      sx={{
                        minWidth: 100,
                        color: slot.available
                          ? 'white'
                          : 'rgba(255, 255, 255, 0.3)',
                        borderColor: slot.available
                          ? 'rgba(255, 255, 255, 0.3)'
                          : 'rgba(255, 255, 255, 0.1)',
                        bgcolor:
                          selectedTime === slot.time
                            ? theme.palette.error.main
                            : 'transparent',
                        '&:hover': {
                          bgcolor: slot.available
                            ? alpha(theme.palette.error.main, 0.1)
                            : 'transparent',
                          borderColor: slot.available
                            ? theme.palette.error.main
                            : 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      {slot.time}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            {/* User Information */}
            <Box>
              <Typography
                variant="subtitle2"
                color="rgba(255, 255, 255, 0.8)"
                gutterBottom
              >
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={userInfo.name}
                    onChange={(e) =>
                      setUserInfo((prev) => ({ ...prev, name: e.target.value }))
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.error.main,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={userInfo.email}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.error.main,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={userInfo.phone}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.error.main,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Special Notes or Requests"
                    value={userInfo.notes}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.error.main,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Right Column - Property Selection */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" color="white" gutterBottom>
              Select Properties to Tour
            </Typography>

            {/* Main Property */}
            <Paper
              sx={{
                p: 2,
                mb: 2,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <LocationOnIcon sx={{ color: theme.palette.error.main }} />
                <Typography variant="subtitle1" color="white" fontWeight={600}>
                  Main Property (Included)
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.8)"
                gutterBottom
              >
                123 Main Street, Beverly Hills, CA
              </Typography>
              <Typography variant="body2" color="white" fontWeight={600}>
                {formatCurrency(propertyPrice)}
              </Typography>
            </Paper>

            {/* Comparable Properties */}
            <Typography
              variant="subtitle2"
              color="rgba(255, 255, 255, 0.8)"
              gutterBottom
            >
              Comparable Properties (Select additional)
            </Typography>

            {comparableProperties.map((property) => (
              <Paper
                key={property.id}
                onClick={() => handlePropertyToggle(property.id)}
                sx={{
                  p: 2,
                  mb: 2,
                  cursor: 'pointer',
                  background: selectedProperties.includes(property.id)
                    ? `linear-gradient(135deg, ${alpha(
                        theme.palette.error.main,
                        0.1
                      )} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`
                    : 'rgba(255, 255, 255, 0.05)',
                  border: `2px solid ${
                    selectedProperties.includes(property.id)
                      ? theme.palette.error.main
                      : 'rgba(255, 255, 255, 0.1)'
                  }`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.error.main,
                      0.05
                    )} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
                    borderColor: theme.palette.error.main,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="rgba(255, 255, 255, 0.8)"
                      gutterBottom
                    >
                      {property.address}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Typography
                        variant="caption"
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        {property.beds} beds
                      </Typography>
                      <Typography
                        variant="caption"
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        {property.baths} baths
                      </Typography>
                      <Typography
                        variant="caption"
                        color="rgba(255, 255, 255, 0.6)"
                      >
                        {property.sqft} sqft
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="white" fontWeight={600}>
                      {formatCurrency(property.price)}
                    </Typography>
                  </Box>
                  <Chip
                    label="1 Credit"
                    size="small"
                    sx={{
                      bgcolor: selectedProperties.includes(property.id)
                        ? theme.palette.error.main
                        : alpha(theme.palette.error.main, 0.2),
                      color: 'white',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              </Paper>
            ))}

            {/* Credits Summary */}
            <Paper
              sx={{
                p: 2,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" color="white" gutterBottom>
                Credits Summary
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                  Total Credits Used
                </Typography>
                <Typography
                  variant="h6"
                  color={hasEnoughCredits ? 'white' : theme.palette.error.main}
                  fontWeight={600}
                >
                  {totalCreditsNeeded} / 6
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="rgba(255, 255, 255, 0.6)"
                display="block"
                sx={{ mt: 1 }}
              >
                {hasEnoughCredits
                  ? `${6 - totalCreditsNeeded} credits remaining`
                  : 'Not enough credits. Purchase more credits to continue.'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': { color: 'white' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            !selectedDate ||
            !selectedTime ||
            !hasEnoughCredits ||
            !userInfo.name ||
            !userInfo.email
          }
          sx={{
            background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(135deg, #c2185b 0%, #ad1457 100%)',
            },
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          Schedule Tour
        </Button>
      </DialogActions>
    </Dialog>
  );
};
